import { MD5 } from 'crypto-js';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInit } from '../../../Contexts/InitProvider';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../utils/Button';
import FocusControl from '../../../utils/FocusControl';
import InputField, {
    InputCheckbox,
    InputWithCustomerSearch,
    SelectOption,
    TextareaField,
    addOption
} from '../../../utils/InputField';
import RadioInput from '../../../utils/RadioInput';
import { CrossIcon, FolderOpenIcon, ImageUploadingIcon } from '../../../utils/SVGIcon';
import { GetFileSize, GetFileTypeToFull } from '../../../utils/Show';
import { mkRelativeUrl } from '../../../utils/URL';
import cn from '../../../utils/cn';
import {
    ApiIssueSupportTicket,
    ApiLoadOptionalData,
    ApiUploadSupportTicketAttachment
} from '../SupportTicketOperation';

const initialState = {
    services: [],
    subscriptions: []
};

// Uploading  Temp Loading
function UploadingTempLoading({ handleFileChange, isLoading }) {
    return (
        <>
            {isLoading && (
                <div className="size-22 text-start bg-transparent text-secondary rounded-md absolute z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ">
                    <ImageUploadingIcon />
                </div>
            )}
            <label
                htmlFor="attachmentFile"
                className={cn(
                    'flex items-center gap-2 bg-secondary-200 px-2 py-1 rounded-md text-secondary cursor-pointer',
                    {
                        'pointer-events-none select-none bg-primary-200 text-gray-400': isLoading
                    }
                )}
            >
                + Add Attachment or Drop Here{' '}
                <span className={cn('text-error', { 'text-gray-400': isLoading })}>
                    (Max 100MB)
                </span>
                <input
                    id="attachmentFile"
                    type="file"
                    className="w-full h-12 border border-gray-300 rounded-md px-4 hidden"
                    onChange={handleFileChange}
                    accept="image/*, .pdf, .doc, .docx,.xls, .xlsx, .txt, .jpg, .jpeg, .png, .mp4, .avi, .wmv, .mov, .3gp, .3g2, .ogg, .webm, .mp3, .wav, .mp4"
                    data-max-files="5"
                />
            </label>
        </>
    );
}

function FileAttachment({ formData, setFormData }) {
    const [tempAttachment, setTempAttachment] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const { Attachment } = formData;
    const [error, setError] = useState('');

    // Handle File Change and Upload
    const handleFileChange = (e) => {
        // Check total file size
        if (Attachment.length >= 5) {
            setError('You can upload maximum 5 files');
            return;
        }
        // Data Destructuring
        const { files } = e.target;
        // Check total selected files error handling
        if (files.length === 0) return;

        // check same file name
        const isSameFile = tempAttachment?.some((item) => item.name === files[0].name);
        if (isSameFile) {
            setError('You have already selected this file');
            return;
        }
        // only file upload 500bytes to 100MB
        if (files?.[0] && files[0].size <= 500) {
            setError('File size too small, minimum file size 500 bytes');
            return;
        }
        if (files?.[0] && files[0].size >= 100000000) {
            setError('File size too large, maximum file size 100MB');
            return;
        }
        const temFile = new FormData();
        temFile.append('attachment', files[0]);
        setIsUploading(true);
        ApiUploadSupportTicketAttachment(temFile, setError)
            .then(({ data }) => {
                setIsUploading(false);
                setFormData((d) => ({
                    ...d,
                    Attachment: [...d.Attachment, data.fileUrl]
                }));
                setTempAttachment((d) => [...d, files[0]]);
            })
            .catch((err) => {
                setError(err.toString(0));
                setIsUploading(false);
            });
    };
    // Remove File from tempAttachment and Attachment
    const removeFile = (indexToRemove) => {
        setFormData((prevFormData) => {
            const updatedAttachment = prevFormData.Attachment.filter(
                (_, index) => index !== indexToRemove
            );
            return { ...prevFormData, Attachment: updatedAttachment };
        });

        setTempAttachment((prevTempAttachment) => {
            const updatedTempAttachment = prevTempAttachment.filter(
                (_, index) => index !== indexToRemove
            );
            return updatedTempAttachment;
        });
    };
    // Get File from Drag over file
    const handleDragOver = (e) => {
        e?.preventDefault();
    };
    // Get File from Drop file and upload
    const handleDrop = (e) => {
        e?.preventDefault();
        const { files } = e.dataTransfer;
        handleFileChange({ target: { files } });
    };
    // 3 sec error message hide
    useEffect(() => {
        if (error) {
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    }, [error]);

    return (
        <div>
            <div>
                <p className="pl-2 text-base capitalize h-auto w-full font-normal">
                    Attachments <span className="">(Optional)</span>
                </p>

                <div
                    className="flex flex-col gap-2 border border-primary-200 px-2 py-3 mb-2 rounded-lg relative transition-all"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    {tempAttachment?.map((item, indexToRemove) => (
                        <div
                            key={MD5(item + indexToRemove)}
                            className="flex justify-between items-center gap-2 bg-secondary-100 px-2 py-1 rounded-md"
                        >
                            <div className="flex justify-start items-center gap-2 font-medium text-primary-700">
                                <p className="w-3/6 text-sm line-clamp-1">{item?.name}</p>
                                <span className="text-xs leading-5 text-primary-700/70 whitespace-nowrap">
                                    {GetFileSize(item?.size)}
                                </span>
                                <span className="text-xs text-primary-700/70 whitespace-nowrap">
                                    {GetFileTypeToFull(item?.type)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* View Attachment */}
                                <button
                                    type="button"
                                    className="text-sm size-7 p-1 text-primary"
                                    onClick={() => {
                                        const url = URL.createObjectURL(item);
                                        window.open(url, '_blank');
                                    }}
                                >
                                    <FolderOpenIcon />
                                </button>
                                {/* Remove attachment */}
                                <button
                                    type="button"
                                    className="text-error"
                                    onClick={() => removeFile(indexToRemove)}
                                >
                                    <CrossIcon />
                                </button>
                            </div>
                        </div>
                    ))}
                    <UploadingTempLoading
                        handleFileChange={handleFileChange}
                        isLoading={isUploading}
                    />
                </div>
            </div>
            <p className="text-sm text-error px-2 mb-1">{error}</p>
        </div>
    );
}

function AddIssueTicketModal({ closeModal, handleReload, type = '', userId = 0, ssId = 0 }) {
    const { initInfo } = useInit();
    const { setMessage } = useTemplate();
    const [optionalData, setOptionalData] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState('');
    const [formData, setFormData] = useState({
        UserId: userId,
        Subject: '',
        Message: '',
        Department: '',
        SsId: ssId,
        Attachment: [],
        IsUrgent: false,
        IsAgree: false,
        Type: type
    });

    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'IsAgree') {
            setFormData((d) => ({ ...d, [name]: !formData.IsAgree }));
            return;
        }

        if (name === 'IsUrgent') {
            setFormData((d) => ({ ...d, [name]: !formData.IsUrgent }));
            return;
        }
        setFormData((d) => ({ ...d, [name]: value }));
    };
    const handleSave = (e) => {
        setLoading(true);
        e.preventDefault();

        const payload = {
            UserId: Number(userId),
            SsId: ssId || Number(formData.SsId),
            ...formData
        };

        ApiIssueSupportTicket(payload, setIsError)
            .then(({ title, data }) => {
                handleReload();
                closeModal();
                setMessage({ type: 'success', text: title });

                // Navigate to ticket details page
                navigate(mkRelativeUrl(`/support-ticket/${data?.id}/info`));
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error.toString() });
            });
    };

    // -- Load optional data of user ex: subscriptions, services
    useEffect(() => {
        if (formData?.UserId) {
            ApiLoadOptionalData(formData?.UserId)
                .then(({ data }) => {
                    setOptionalData(data);
                })
                .catch((err) => {
                    setOptionalData(initialState);
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    }, [formData?.UserId, setMessage]);

    // Focus Control on Error
    FocusControl(isError, setIsError, formData);
    return (
        <form onSubmit={handleSave} className="flex flex-col gap-2">
            {userId === 0 ? (
                <InputWithCustomerSearch
                    label="Select Customer"
                    name="UserId"
                    formData={formData}
                    onChange={handleChange}
                    required
                />
            ) : null}

            <div className="flex gap-2">
                <RadioInput
                    name="Type"
                    label="None"
                    value=""
                    onClick={handleChange}
                    checked={formData?.Type === ''}
                    disabled={ssId !== 0}
                />
                <RadioInput
                    name="Type"
                    label="Subscription"
                    value="subscription"
                    onClick={handleChange}
                    checked={formData?.Type === 'subscription'}
                    disabled={ssId !== 0}
                />
                <RadioInput
                    name="Type"
                    label="Service"
                    value="service"
                    onClick={handleChange}
                    checked={formData?.Type === 'service'}
                    disabled={ssId !== 0}
                />
            </div>

            {formData?.Type === 'subscription' ? (
                <SelectOption
                    label="Subscription"
                    name="SsId"
                    optional
                    formData={formData}
                    onChange={handleChange}
                    options={optionalData?.subscriptions?.map((item) =>
                        addOption(item.id, item.subscriptionName, item.id)
                    )}
                    disabled={ssId !== 0}
                />
            ) : null}
            {formData?.Type === 'service' ? (
                <SelectOption
                    label="Service"
                    name="SsId"
                    optional
                    formData={formData}
                    onChange={handleChange}
                    options={optionalData?.services?.map((item) =>
                        addOption(item.id, item.serviceName, item.id)
                    )}
                    disabled={ssId !== 0}
                />
            ) : null}

            <SelectOption
                label="Department"
                name="Department"
                formData={formData}
                onChange={handleChange}
                options={initInfo?.supportDepartments?.map((item) =>
                    addOption(item.toLowerCase(), item, item.toLowerCase())
                )}
                required
            />

            <InputField
                label="Subject"
                name="Subject"
                placeholder="Type subject..."
                formData={formData}
                onChange={handleChange}
                required
            />

            <TextareaField
                label="Message"
                name="Message"
                placeholder="Write your message..."
                formData={formData}
                onChange={handleChange}
                height="h-28"
                required
            />
            <FileAttachment formData={formData} setFormData={setFormData} />
            <InputCheckbox
                label="IsUrgent"
                name="IsUrgent"
                formData={formData}
                onChange={handleChange}
                text="Is this issue urgent?"
                className="items-start [&>input]:mt-1 [&>svg]:top-2 cursor-pointer"
            />
            <InputCheckbox
                label="IsAgree"
                name="IsAgree"
                formData={formData}
                onChange={handleChange}
                text="Support ticket will be resolved according to support ticket policy."
            />

            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={closeModal} />
                <ButtonPrimary
                    title="Submit"
                    btnType="submit"
                    disabled={!formData?.IsAgree}
                    loading={loading}
                />
            </div>
        </form>
    );
}

export default AddIssueTicketModal;
